<template>
  <div class="lookup-page">
    <v-card class="lookup-card">
      <div class="lookup-header">
        <div class="lookup-header__copy">
          <div class="lookup-title">Ethnicity</div>
          <div class="lookup-subtitle">Maintain ethnicity options used by preservice forms.</div>
        </div>
        <v-text-field
            v-model="search"
            class="lookup-search"
            append-icon="mdi-magnify"
            hide-details
            label="Search"
            single-line
        ></v-text-field>
        <v-btn
            v-if="canCreateEthnicity"
            class="lookup-add-button"
            depressed
            @click="addEthnicity()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add Ethnicity</span>
        </v-btn>
      </div>
      <v-data-table
          :headers="headers"
          :items="ethnicities.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="ethnicities.count"
          class="lookup-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.name`]="{ item }">
          <label for="item">{{ item.name ? item.name : "-" }}</label>
        </template>
        <template v-slot:[`item.code`]="{ item }">
          <label for="item">{{ item.code ? item.code : "-" }}</label>
        </template>
        <template v-if="canEditEthnicity" v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon class="mr-2 lookup-action-icon" @click="editEthnicity(item)" v-on="on">
                mdi-circle-edit-outline
              </v-icon>
            </template>
            <span>{{ title }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.isActive`]="{ item }">
          <v-chip :color="getColor(item.isActive)" dark>
            {{ item.isActive === true ? "Active" : "Not Active" }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent width="50%">
      <v-card class="lookup-dialog-card">
        <v-card-title class="lookup-dialog__title" primary-title>
          {{ title }}
          <v-spacer></v-spacer>
          <v-btn class="lookup-dialog__close" color="white" text @click="closeDialog()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <AddEthnicity
            v-if="dialog"
            :ethnicity="ethnicity"
            @close="closeDialog()"
            @registerOrUpdateEthnicity="registerOrUpdateEthnicity"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {getUser} from "@/utils/helper";
import {LookupService} from "../../../common/api.service";
import AddEthnicity from "./add-ethnicity.vue";
import {EthnicityService} from "./ethnicity.service";

export default {
  components: {
    AddEthnicity
  },
  data() {
    return {
      search: "",
      searchDebounce: null,
      loading: false,
      headers: [
        {text: "Ethnicity Name", value: "name"},
        {text: "Ethnicity Code", value: "code"},
        {text: "Ethnicity Status", value: "isActive"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      ethnicities: {rows: [], count: 0},
      ethnicity: null,
      dialog: false,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      title: ""
    }
  },
  computed: {
    canCreateEthnicity() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Ethnicity" &&
              permission.canCreate
      );
    },
    canEditEthnicity() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Ethnicity" &&
              permission.canUpdate
      );
    },
  },
  async beforeMount() {
    this.fetchEthnicities();
  },
  watch: {
    options: {
      handler() {
        this.fetchEthnicities();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchEthnicities();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchEthnicities();
      }, 400);
    },
  },
  methods: {
    async fetchEthnicities() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;
      const res = await LookupService.getEthnicitiesWP(
          page,
          itemsPerPage,
          this.search
      );
      if (res.status === 200) {
        this.ethnicities = res.data;
      } else {
        this.$toast.error("There was a problem fetching ethnicities, Please try Again!");
      }
      this.loading = false;
    },
    addEthnicity() {
      this.ethnicity = null;
      this.dialog = true;
      this.title = "Add Ethnicity";
    },
    editEthnicity(item) {
      this.ethnicity = item;
      this.dialog = true;
      this.title = "Edit Ethnicity";
    },
    closeDialog() {
      this.dialog = false;
      this.fetchEthnicities();
    },
    getColor(isActive) {
      return isActive === false ? "red" : "green";
    },
    async registerOrUpdateEthnicity(ethnicity) {
      this.loading = true;
      let res = null;

      if (ethnicity.editMode) {
        res = await EthnicityService.update(ethnicity.id, ethnicity);

        if (res.status === 201) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to update ethnicity, please try again");
        }
      } else {
        res = await EthnicityService.create(ethnicity);

        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to create ethnicity, please try again");
        }
      }

      this.fetchEthnicities();
      this.loading = false;
    }
  }
}
</script>

<style scoped>
.lookup-page {
  padding: 24px;
}

.lookup-card {
  border-radius: 20px;
  padding: 8px 8px 16px;
}

.lookup-header {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 280px) auto;
  padding: 20px 24px 12px;
}

.lookup-header__copy {
  min-width: 0;
}

.lookup-title {
  color: #0f172a;
  font-size: 1.3rem;
  font-weight: 700;
}

.lookup-subtitle {
  color: #64748b;
  font-size: 0.95rem;
  margin-top: 4px;
}

.lookup-search {
  margin: 0;
}

.lookup-add-button {
  background: #0f766e !important;
  color: #fff !important;
  justify-self: end;
}

.lookup-table {
  border-radius: 16px;
  overflow: hidden;
}

.lookup-action-icon {
  color: #0f766e;
  cursor: pointer;
}

.lookup-dialog-card {
  border-radius: 20px;
}

.lookup-dialog__title {
  background: linear-gradient(135deg, #0f766e, #0ea5a4);
  color: #fff;
}

.lookup-dialog__close {
  min-width: 36px;
}

@media (max-width: 960px) {
  .lookup-header {
    grid-template-columns: 1fr;
  }

  .lookup-add-button {
    justify-self: stretch;
  }
}
</style>
